// src/components/roads/drive/DetectionReviewModal.js
// --- UPDATE (Drive Mode AI review) -----------------------------------------------------
// Enlarged Drive Mode photo with the AI boxes, everything known about the detection, and the
// review that teaches the model:
//   ✓ Confirm      the AI was right            → labelled training example, segment health
//   ✎ Correct to   it is damage, another type  → corrected training example
//   ✗ Not damage   false alarm                 → "background" example (teaches what is NOT damage)
// Keyboard: C confirm · X not damage · ← / → previous / next · B boxes on/off.
// --- END UPDATE ------------------------------------------------------------------------
import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalCloseButton, ModalFooter,
    Box, Flex, HStack, VStack, Text, Badge, Button, Select, SimpleGrid, IconButton, Icon, Switch, Tooltip,
    useColorModeValue, useToast, Divider,
} from '@chakra-ui/react';
import {
    MdCheckCircle, MdCancel, MdEdit, MdChevronLeft, MdChevronRight, MdPlace, MdSchool, MdOpenInNew,
} from 'react-icons/md';
import DetectionImage from './DetectionImage';
import { DAMAGE_KEYS, damageInfo, verdictInfo, reviewDriveFinding, pct, fmtWhen, fmtM } from './driveAiApi';

const Fact = ({ label, children, muted }) => (
    <Box>
        <Text fontSize="2xs" fontWeight="700" textTransform="uppercase" color={muted}>{label}</Text>
        <Text fontSize="sm" fontWeight="700">{children}</Text>
    </Box>
);
Fact.propTypes = { label: PropTypes.string, children: PropTypes.node, muted: PropTypes.string };

const DetectionReviewModal = ({ isOpen, onClose, findings = [], index = 0, onIndexChange, onReviewed }) => {
    const toast = useToast();
    const [busy, setBusy] = useState(null);
    const [showBoxes, setShowBoxes] = useState(true);
    const [correctTo, setCorrectTo] = useState('');
    const muted = useColorModeValue('gray.500', 'gray.400');
    const panelBg = useColorModeValue('gray.50', 'whiteAlpha.50');
    const f = findings[index] || null;

    useEffect(() => { setCorrectTo(''); }, [f?.id]);

    const go = useCallback((d) => {
        if (!findings.length) return;
        onIndexChange?.((index + d + findings.length) % findings.length);
    }, [findings.length, index, onIndexChange]);

    const review = useCallback(async (verdict, label) => {
        if (!f || busy) return;
        setBusy(verdict);
        try {
            const res = await reviewDriveFinding(f.id, verdict, label);
            const updated = res?.data || null;
            toast({
                title: verdict === 'reject' ? 'Marked as not damage' : verdict === 'correct' ? `Corrected to ${damageInfo(label).label}` : 'Confirmed',
                description: res?.training?.note || 'Segment health will update in a moment.',
                status: 'success', duration: 3000,
            });
            onReviewed?.(updated, f);
            // move on to the next photo that still needs review
            const nextIdx = findings.findIndex((x, i) => i > index && x.verdict === 'reported');
            if (nextIdx >= 0) onIndexChange?.(nextIdx);
        } catch (e) {
            toast({ title: 'Review not saved', description: e.message, status: 'error', duration: 5000 });
        } finally {
            setBusy(null);
        }
    }, [f, busy, toast, onReviewed, findings, index, onIndexChange]);

    useEffect(() => {
        if (!isOpen) return undefined;
        const onKey = (e) => {
            if (e.target && ['INPUT', 'SELECT', 'TEXTAREA'].includes(e.target.tagName)) return;
            if (e.key === 'ArrowRight') go(1);
            else if (e.key === 'ArrowLeft') go(-1);
            else if (e.key === 'c' || e.key === 'C') review('confirm');
            else if (e.key === 'x' || e.key === 'X') review('reject');
            else if (e.key === 'b' || e.key === 'B') setShowBoxes((v) => !v);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [isOpen, go, review]);

    if (!f) return null;
    const dmg = damageInfo(f.damage_type);
    const vi = verdictInfo(f.verdict);
    const reviewed = f.verdict !== 'reported';
    const mapUrl = f.latitude != null && f.longitude != null ? `https://www.google.com/maps?q=${f.latitude},${f.longitude}` : null;

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="6xl" isCentered scrollBehavior="inside">
            <ModalOverlay bg="blackAlpha.700" />
            <ModalContent>
                <ModalHeader pr={12}>
                    <HStack spacing={2} wrap="wrap">
                        <Box w={3} h={3} borderRadius="full" bg={dmg.color} />
                        <Text>{dmg.label}</Text>
                        <Badge colorScheme={vi.scheme}>{vi.label}</Badge>
                        {f.ai_damage_type && f.ai_damage_type !== f.damage_type && (
                            <Badge variant="outline" colorScheme="blue">AI said {damageInfo(f.ai_damage_type).label}</Badge>
                        )}
                        <Badge variant="subtle">{f.source === 'technician' ? 'Technician photo' : 'Drive Mode AI'}</Badge>
                        <Text fontSize="xs" color={muted} fontWeight="500">{index + 1} / {findings.length}</Text>
                    </HStack>
                </ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    <Flex gap={4} direction={{ base: 'column', lg: 'row' }}>
                        <Box flex={3} minW={0} position="relative">
                            <DetectionImage finding={f} showBoxes={showBoxes} maxH="68vh" rounded="xl" highlightType={f.damage_type} />
                            {findings.length > 1 && (
                                <>
                                    <IconButton aria-label="Previous photo" icon={<Icon as={MdChevronLeft} boxSize={6} />} position="absolute" left={2} top="50%" transform="translateY(-50%)" borderRadius="full" bg="blackAlpha.600" color="white" _hover={{ bg: 'blackAlpha.800' }} onClick={() => go(-1)} />
                                    <IconButton aria-label="Next photo" icon={<Icon as={MdChevronRight} boxSize={6} />} position="absolute" right={2} top="50%" transform="translateY(-50%)" borderRadius="full" bg="blackAlpha.600" color="white" _hover={{ bg: 'blackAlpha.800' }} onClick={() => go(1)} />
                                </>
                            )}
                            <HStack mt={2} spacing={2}>
                                <Switch size="sm" isChecked={showBoxes} onChange={(e) => setShowBoxes(e.target.checked)} id="dm-boxes" />
                                <Text as="label" htmlFor="dm-boxes" fontSize="xs" color={muted}>Show AI boxes (B)</Text>
                                <Text fontSize="xs" color={muted} ml="auto">C confirm · X not damage · ← → browse</Text>
                            </HStack>
                        </Box>
                        <VStack flex={2} minW={{ lg: '300px' }} align="stretch" spacing={3}>
                            <Box bg={panelBg} p={3} borderRadius="lg">
                                <Text fontSize="xs" fontWeight="800" textTransform="uppercase" color={muted} mb={2}>AI prediction</Text>
                                {(f.detections || []).length === 0 ? (
                                    <Text fontSize="sm" color={muted}>{f.source === 'technician' ? 'Labelled by the technician — no AI boxes on this photo.' : 'No boxes were sent with this photo.'}</Text>
                                ) : (
                                    <VStack align="stretch" spacing={1}>
                                        {f.detections.map((d, i) => (
                                            <HStack key={i} spacing={2}>
                                                <Box w={2.5} h={2.5} borderRadius="sm" bg={damageInfo(d.label).color} />
                                                <Text fontSize="sm" flex={1}>{damageInfo(d.label).label}</Text>
                                                <Badge colorScheme={d.confidence >= 0.75 ? 'green' : d.confidence >= 0.5 ? 'yellow' : 'orange'}>{pct(d.confidence)}</Badge>
                                            </HStack>
                                        ))}
                                    </VStack>
                                )}
                            </Box>
                            <SimpleGrid columns={2} spacing={3} bg={panelBg} p={3} borderRadius="lg">
                                <Fact label="Road" muted={muted}>{f.road_name || (f.road_id ? `#${f.road_id}` : 'Not on a mapped road')}</Fact>
                                <Fact label="Segment" muted={muted}>{f.segment_index != null ? `Segment ${f.segment_index}` : '—'}</Fact>
                                <Fact label="Chainage" muted={muted}>{fmtM(f.chainage_m)}</Fact>
                                <Fact label="From centre line" muted={muted}>{f.offset_m != null ? `${f.offset_m} m` : '—'}</Fact>
                                <Fact label="Severity" muted={muted}>{f.severity || '—'}</Fact>
                                <Fact label="AI confidence" muted={muted}>{pct(f.confidence)}</Fact>
                                <Fact label="Taken" muted={muted}>{fmtWhen(f.created_at)}</Fact>
                                <Fact label="GPS accuracy" muted={muted}>{f.accuracy_m != null ? `±${Math.round(f.accuracy_m)} m` : '—'}</Fact>
                            </SimpleGrid>
                            <HStack spacing={2} fontSize="xs" color={muted} wrap="wrap">
                                <Icon as={MdPlace} />
                                <Text>{f.latitude != null ? `${f.latitude.toFixed(6)}, ${f.longitude.toFixed(6)}` : 'No GPS'}</Text>
                                {mapUrl && <Button as="a" href={mapUrl} target="_blank" rel="noopener noreferrer" size="xs" variant="link" rightIcon={<Icon as={MdOpenInNew} />}>Map</Button>}
                            </HStack>
                            {f.note && <Text fontSize="xs" color={muted}>Note: {f.note}</Text>}
                            {reviewed && (
                                <Text fontSize="xs" color={muted}>
                                    {vi.label}{f.reviewed_by ? ` by ${f.reviewed_by}` : ''}{f.reviewed_at ? ` · ${fmtWhen(f.reviewed_at)}` : ''}. You can change the answer.
                                </Text>
                            )}
                            <HStack spacing={1} fontSize="xs" color={muted}>
                                <Icon as={MdSchool} color="purple.400" />
                                <Text>{f.vision_sample_id ? 'This photo is in the detection model\'s training set.' : 'Your answer adds this photo to the training set.'}</Text>
                            </HStack>
                        </VStack>
                    </Flex>
                </ModalBody>
                <Divider />
                <ModalFooter gap={2} flexWrap="wrap">
                    <Tooltip hasArrow label="The AI was right — teaches the model and updates the segment's health (C)">
                        <Button colorScheme="green" leftIcon={<Icon as={MdCheckCircle} />} onClick={() => review('confirm')} isLoading={busy === 'confirm'} isDisabled={!!busy && busy !== 'confirm'}>
                            Confirm {dmg.label.toLowerCase()}
                        </Button>
                    </Tooltip>
                    <HStack spacing={1}>
                        <Select size="md" w="220px" placeholder="Correct to…" value={correctTo} onChange={(e) => setCorrectTo(e.target.value)} aria-label="Correct damage type">
                            {DAMAGE_KEYS.filter((k) => k !== f.damage_type).map((k) => <option key={k} value={k}>{damageInfo(k).label}</option>)}
                        </Select>
                        <Button leftIcon={<Icon as={MdEdit} />} colorScheme="blue" variant="outline" isDisabled={!correctTo || (!!busy && busy !== 'correct')} isLoading={busy === 'correct'} onClick={() => review('correct', correctTo)}>
                            Save
                        </Button>
                    </HStack>
                    <Tooltip hasArrow label="False alarm — teaches the model what is NOT road damage (X)">
                        <Button colorScheme="gray" variant="outline" leftIcon={<Icon as={MdCancel} />} onClick={() => review('reject')} isLoading={busy === 'reject'} isDisabled={!!busy && busy !== 'reject'}>
                            Not damage
                        </Button>
                    </Tooltip>
                    <Button variant="ghost" ml="auto" onClick={onClose}>Close</Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

DetectionReviewModal.propTypes = {
    isOpen: PropTypes.bool,
    onClose: PropTypes.func.isRequired,
    findings: PropTypes.array,
    index: PropTypes.number,
    onIndexChange: PropTypes.func,
    onReviewed: PropTypes.func,
};

export default DetectionReviewModal;
